import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { normalizeRsvpFields, RSVP_FIELD_DEFS, DRINK_PREFERENCE_OPTIONS, FOOD_PREFERENCE_OPTIONS, getCustomRsvpFields } from '../constants/rsvpFields';
import { clearRsvpDraft, loadRsvpDraft, saveRsvpDraft } from '../utils/rsvpDraftCache';

const emptyGuest = () => ({ name: '', age: '', foodPreference: '', drinkPreference: '' });

const isPresetOption = (options, value) => options.some((opt) => opt.id === value);

const PreferenceChoice = ({ label, required, options, value, onChange, placeholder }) => {
  const presetSelected = isPresetOption(options, value);
  const customValue = presetSelected ? '' : (value || '');
  const [customOpen, setCustomOpen] = useState(Boolean(customValue));

  useEffect(() => {
    if (customValue) setCustomOpen(true);
  }, [customValue]);

  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}{required && <span className="text-rose-500"> *</span>}
      </label>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const selected = value === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => {
                setCustomOpen(false);
                onChange(opt.id);
              }}
              className={`px-3 py-2 text-xs font-semibold rounded-lg border transition-colors ${
                selected
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
              }`}
            >
              {opt.label}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => {
            setCustomOpen(true);
            if (presetSelected) onChange('');
          }}
          className={`px-3 py-2 text-xs font-semibold rounded-lg border transition-colors ${
            customOpen
              ? 'bg-emerald-600 text-white border-emerald-600'
              : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
          }`}
        >
          Add your own
        </button>
      </div>
      {customOpen && (
        <input
          type="text"
          value={customValue}
          onChange={(e) => onChange(e.target.value)}
          className="mt-2 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
          placeholder={placeholder}
        />
      )}
    </div>
  );
};

const buildFormState = (source, fields) => {
  const count = source?.guestCount || 1;
  const existingGuests = Array.isArray(source?.guests) ? source.guests : [];
  const size = fields.guestCount?.enabled
    ? Math.max(1, Math.min(20, Number(count) || 1))
    : Math.max(1, existingGuests.length || 1);
  const guests = Array.from({ length: size }, (_, i) => ({
    name: existingGuests[i]?.name || '',
    age: existingGuests[i]?.age != null ? String(existingGuests[i].age) : '',
    foodPreference: existingGuests[i]?.foodPreference || '',
    drinkPreference: existingGuests[i]?.drinkPreference || '',
  }));
  return { guestCount: size, guests };
};

const RsvpDetailsModal = ({
  isOpen,
  onClose,
  onSubmit,
  eventId,
  userId,
  eventName = 'Event',
  rsvpFields,
  saving = false,
  initialDetails = null,
  isEditing = false,
}) => {
  const fields = useMemo(() => normalizeRsvpFields(rsvpFields), [rsvpFields]);
  const [guestCount, setGuestCount] = useState(1);
  const [guests, setGuests] = useState([emptyGuest()]);
  const [customAnswers, setCustomAnswers] = useState({});
  const [error, setError] = useState('');
  const [restoredDraft, setRestoredDraft] = useState(false);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      initializedRef.current = false;
      setRestoredDraft(false);
      return;
    }
    if (initializedRef.current) return;
    initializedRef.current = true;

    const cached = loadRsvpDraft(eventId, userId);
    // When editing an existing RSVP, prefer saved server answers over a local draft.
    const source = isEditing
      ? (initialDetails || cached)
      : (cached || initialDetails);
    const next = buildFormState(source, fields);
    setGuestCount(next.guestCount);
    setGuests(next.guests);
    const answers = {};
    const saved = source?.customAnswers;
    if (Array.isArray(saved)) {
      saved.forEach((item) => {
        if (item?.id) answers[item.id] = item.value != null ? String(item.value) : '';
      });
    } else if (saved && typeof saved === 'object') {
      Object.entries(saved).forEach(([id, value]) => {
        answers[id] = value != null ? String(value) : '';
      });
    }
    getCustomRsvpFields(fields).forEach((field) => {
      if (answers[field.id] == null) answers[field.id] = '';
    });
    setCustomAnswers(answers);
    setRestoredDraft(Boolean(cached) && !isEditing);
    setError('');
  }, [isOpen, eventId, userId, initialDetails, fields, isEditing]);

  useEffect(() => {
    if (!isOpen || !eventId || !userId) return;
    saveRsvpDraft(eventId, userId, { guestCount, guests, customAnswers });
  }, [guestCount, guests, customAnswers, isOpen, eventId, userId]);

  useEffect(() => {
    if (!fields.guestCount?.enabled) return;
    const size = Math.max(1, Math.min(20, Number(guestCount) || 1));
    setGuests((prev) => {
      if (prev.length === size) return prev;
      if (prev.length < size) {
        return [...prev, ...Array.from({ length: size - prev.length }, emptyGuest)];
      }
      return prev.slice(0, size);
    });
  }, [guestCount, fields.guestCount?.enabled]);

  if (!isOpen) return null;

  const customFields = getCustomRsvpFields(fields);

  const needsGuestRows = fields.guestNames?.enabled
    || fields.guestAges?.enabled
    || fields.foodPreference?.enabled
    || fields.drinkPreference?.enabled;

  const updateGuest = (index, patch) => {
    setGuests((prev) => prev.map((g, i) => (i === index ? { ...g, ...patch } : g)));
  };

  const validate = () => {
    if (fields.guestCount?.enabled && fields.guestCount?.required) {
      const count = Number(guestCount);
      if (!count || count < 1) {
        setError('Please enter how many people are coming.');
        return false;
      }
    }
    if (needsGuestRows) {
      for (let i = 0; i < guests.length; i += 1) {
        const g = guests[i];
        if (fields.guestNames?.enabled && fields.guestNames?.required && !g.name?.trim()) {
          setError(`Please enter a name for guest ${i + 1}.`);
          return false;
        }
        if (fields.guestAges?.enabled && fields.guestAges?.required && !String(g.age || '').trim()) {
          setError(`Please enter an age for guest ${i + 1}.`);
          return false;
        }
        if (fields.foodPreference?.enabled && fields.foodPreference?.required && !g.foodPreference?.trim()) {
          setError(`Please enter a food preference for guest ${i + 1}.`);
          return false;
        }
        if (fields.drinkPreference?.enabled && fields.drinkPreference?.required && !g.drinkPreference) {
          setError(`Please choose a drink preference for guest ${i + 1}.`);
          return false;
        }
      }
    }
    for (let i = 0; i < customFields.length; i += 1) {
      const field = customFields[i];
      if (field.required && !String(customAnswers[field.id] || '').trim()) {
        setError(`Please answer “${field.label}”.`);
        return false;
      }
    }
    setError('');
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const payload = {};
    if (fields.guestCount?.enabled) {
      payload.guestCount = Number(guestCount) || guests.length;
    }
    if (needsGuestRows) {
      payload.guests = guests.map((g) => ({
        name: g.name?.trim() || '',
        age: g.age !== '' ? Number(g.age) : null,
        foodPreference: g.foodPreference?.trim() || '',
        drinkPreference: g.drinkPreference?.trim() || '',
      }));
    }
    if (customFields.length) {
      payload.customAnswers = customFields.map((field) => ({
        id: field.id,
        label: field.label,
        value: String(customAnswers[field.id] || '').trim(),
      }));
    }
    clearRsvpDraft(eventId, userId);
    onSubmit?.(payload);
  };

  const handleCancel = () => {
    saveRsvpDraft(eventId, userId, { guestCount, guests, customAnswers });
    onClose?.();
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[1400] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="RSVP details"
    >
      <form
        className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white dark:bg-gray-900 shadow-2xl border border-gray-200 dark:border-gray-700"
        onSubmit={handleSubmit}
      >
        <div className="sticky top-0 z-10 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 px-5 py-4 text-white rounded-t-2xl">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold">
                {isEditing ? 'Update your RSVP' : "You're coming! 🎉"}
              </h2>
              <p className="text-sm text-white/85 mt-0.5">
                {isEditing ? (
                  <>Change your details for <strong>{eventName}</strong></>
                ) : (
                  <>Tell us a bit more for <strong>{eventName}</strong></>
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="flex-shrink-0 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {restoredDraft && (
            <p className="text-xs font-medium text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/50 rounded-lg px-3 py-2">
              Your previous answers were restored. Changes are saved automatically.
            </p>
          )}

          {!restoredDraft && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Your answers are saved automatically — you won&apos;t lose them if you close this by mistake.
            </p>
          )}

          {fields.guestCount?.enabled && (
            <div>
              <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-1">
                {RSVP_FIELD_DEFS.find((f) => f.id === 'guestCount')?.label}
                {fields.guestCount.required && <span className="text-rose-500"> *</span>}
              </label>
              <input
                type="number"
                min={1}
                max={20}
                value={guestCount}
                onChange={(e) => setGuestCount(e.target.value)}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                placeholder="e.g. 2"
              />
            </div>
          )}

          {needsGuestRows && guests.map((guest, index) => (
            <div key={index} className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 space-y-2 bg-gray-50/80 dark:bg-gray-800/40">
              <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
                Guest {index + 1}
              </p>
              {fields.guestNames?.enabled && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Name{fields.guestNames.required && <span className="text-rose-500"> *</span>}
                  </label>
                  <input
                    type="text"
                    value={guest.name}
                    onChange={(e) => updateGuest(index, { name: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                    placeholder="Full name"
                  />
                </div>
              )}
              {fields.guestAges?.enabled && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Age{fields.guestAges.required && <span className="text-rose-500"> *</span>}
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={120}
                    value={guest.age}
                    onChange={(e) => updateGuest(index, { age: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                    placeholder="Age"
                  />
                </div>
              )}
              {fields.foodPreference?.enabled && (
                <PreferenceChoice
                  label="Food preference"
                  required={fields.foodPreference.required}
                  options={FOOD_PREFERENCE_OPTIONS}
                  value={guest.foodPreference}
                  onChange={(foodPreference) => updateGuest(index, { foodPreference })}
                  placeholder="e.g. Vegan, Jain, No nuts"
                />
              )}
              {fields.drinkPreference?.enabled && (
                <PreferenceChoice
                  label="Drinks"
                  required={fields.drinkPreference.required}
                  options={DRINK_PREFERENCE_OPTIONS}
                  value={guest.drinkPreference}
                  onChange={(drinkPreference) => updateGuest(index, { drinkPreference })}
                  placeholder="e.g. Juice only, Mocktails"
                />
              )}
            </div>
          ))}

          {customFields.map((field) => (
            <div key={field.id}>
              <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-1">
                {field.label}{field.required && <span className="text-rose-500"> *</span>}
              </label>
              <input
                type="text"
                value={customAnswers[field.id] || ''}
                onChange={(e) => setCustomAnswers((prev) => ({ ...prev, [field.id]: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                placeholder={`Your answer for ${field.label}`}
              />
            </div>
          ))}

          {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="flex-1 px-4 py-2.5 text-sm font-semibold rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Save & close
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2.5 text-sm font-semibold rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
            >
              {saving ? 'Saving…' : (isEditing ? 'Save changes' : 'Confirm RSVP')}
            </button>
          </div>
        </div>
      </form>
    </div>,
    document.body,
  );
};

export default RsvpDetailsModal;
